import * as React from 'react';
import {
    Typography,
    Box,
    Container,
    Button,
    CircularProgress,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from '@mui/material';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { centered_flex_box, MainInput, main_button } from '../../app/components/Styles';
import { brandFilterOptions, brandOptionLabel } from '../../app/components/BrandSelect';
import { getVendors, getStockRecountTemplate, submitStockRecount } from '../../app/store/actions/imsAdminActions.js';
import { Download, Upload, Warning, History } from '@mui/icons-material';
import { useEffect } from 'react';
import axios from 'axios';
import ReactFileReader from 'react-file-reader';
import { notification } from 'antd';
import download from 'downloadjs';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import moment from 'moment';

const sortProductsBySku = (productList) => {
    return [...(productList || [])].sort((a, b) => {
        const skuA = (a.sku || '').toString().toLowerCase();
        const skuB = (b.sku || '').toString().toLowerCase();
        return skuA.localeCompare(skuB);
    });
};

const REFRESH_PASSWORD_USER_IDS = ['65cba5b59d76a5203fb0cdae', '68fea2723820930efc7db2b5', '685ea490b34528b6f3841909'];

export const StockRecount = ({
    getVendors,
    getStockRecountTemplate,
    submitStockRecount,
    vendors,
    stockRecountProducts,
    isLoading,
    user,
    token,
}) => {
    const navigate = useNavigate();
    const [vendorId, setVendorId] = React.useState(null);
    const [templateProducts, setTemplateProducts] = React.useState([]);
    const [recountRows, setRecountRows] = React.useState([]);
    const [file, setFile] = React.useState(null);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [refreshDialogOpen, setRefreshDialogOpen] = React.useState(false);
    const [refreshPassword, setRefreshPassword] = React.useState('');
    const [refreshError, setRefreshError] = React.useState(null);
    const [refreshValidated, setRefreshValidated] = React.useState(false);
    const [refreshLoading, setRefreshLoading] = React.useState(false);
    const [pendingSubmission, setPendingSubmission] = React.useState(null);

    const requiresRefreshPassword = React.useMemo(
        () => REFRESH_PASSWORD_USER_IDS.includes(user?._id),
        [user]
    );

    const selectedVendor = React.useMemo(
        () => vendors?.find((vendor) => vendor._id === vendorId),
        [vendors, vendorId]
    );

    useEffect(() => {
        getVendors({ page: 1, limit: 500 });
    }, []);

    useEffect(() => {
        if (!vendorId) {
            setTemplateProducts([]);
            setRecountRows([]);
            setFile(null);
            setRefreshValidated(false);
            return;
        }
        getStockRecountTemplate({ vendorId })
            .then((products) => {
                setTemplateProducts(products || []);
            })
            .catch(() => {
                setTemplateProducts([]);
            });
        setRecountRows([]);
        setFile(null);
        setRefreshValidated(false);
    }, [vendorId]);

    useEffect(() => {
        if (stockRecountProducts?.length && vendorId) {
            setTemplateProducts(stockRecountProducts);
        }
    }, [stockRecountProducts, vendorId]);

    const handleFiles = (files) => {
        setFile(files[0]);
    };

    useEffect(() => {
        if (!file || !templateProducts?.length) {
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                const header = parsedData[0];
                const items = [];
                parsedData.slice(1).forEach((row) => {
                    const item = {};
                    row.forEach((value, index) => {
                        item[header[index]] = value;
                    });
                    const sku = item['Product SKU'];
                    const actualCountRaw = item['Actual Count'];
                    if (actualCountRaw === undefined || actualCountRaw === null || actualCountRaw === '') {
                        return;
                    }
                    const actualCount = parseInt(actualCountRaw, 10);
                    if (Number.isNaN(actualCount)) {
                        return;
                    }
                    const found = templateProducts.find(
                        (product) => product.sku === sku
                    );
                    if (found) {
                        items.push({
                            productId: found.productId,
                            sku: found.sku,
                            name: found.name,
                            onHandBefore: found.onHand,
                            actualCount,
                            delta: actualCount - found.onHand,
                        });
                    }
                });
                setRecountRows(items);
                if (items.length === 0) {
                    notification.warning({ message: 'No valid recount rows found in the uploaded file.' });
                }
            } catch (err) {
                console.log(err);
                notification.error({ message: 'Unable to read Excel file. Please use the downloaded template.' });
            }
        };
        reader.readAsArrayBuffer(file);
    }, [file, templateProducts]);

    const handleExportToExcel = async () => {
        if (!vendorId || !templateProducts?.length) {
            notification.warning({ message: 'Select a brand and wait for products to load before downloading.' });
            return;
        }
        setIsDownloading(true);
        notification.info({ message: 'Downloading stock recount template...' });
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Stock Recount');
        workbook.creator = 'Chainz';

        worksheet.columns = [
            { header: 'Product SKU', key: 'sku' },
            { header: 'Product Name', key: 'name' },
            { header: 'System On Hand', key: 'onHand' },
            { header: 'Actual Count', key: 'actualCount' },
        ];

        sortProductsBySku(templateProducts).forEach((product) => {
            worksheet.addRow({
                sku: product.sku,
                name: product.name,
                onHand: product.onHand,
                actualCount: '',
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const brandName = selectedVendor?.brandName || 'Stock Recount';
        const fileName = `${brandName} Stock Recount - ${moment().format('DD_MM_YYYY')}.xlsx`;
        const blob = new Blob([buffer], { type: mimeType });
        download(blob, fileName, mimeType);
        setIsDownloading(false);
    };

    const buildSubmission = () => ({
        vendorId,
        items: recountRows.map((row) => ({
            productId: row.productId,
            actualCount: row.actualCount,
        })),
    });

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!vendorId || recountRows.length === 0) {
            notification.warning({ message: 'Upload a recount file with at least one actual count.' });
            return;
        }

        const data = buildSubmission();

        if (requiresRefreshPassword && !refreshValidated) {
            setPendingSubmission(data);
            setRefreshDialogOpen(true);
            return;
        }

        submitStockRecount(data, navigate);
    };

    const handleRefreshClose = () => {
        if (refreshLoading) {
            return;
        }
        setRefreshDialogOpen(false);
        setRefreshPassword('');
        setRefreshError(null);
        setPendingSubmission(null);
    };

    const handleRefreshConfirm = async () => {
        if (!refreshPassword) {
            setRefreshError('Outbound password is required.');
            return;
        }
        setRefreshLoading(true);
        setRefreshError(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE}/IMAdmin/outbound/refreshPass`,
                { password: refreshPassword },
                {
                    headers: {
                        'x-access-token': token,
                    },
                }
            );

            if (response.status === 200) {
                setRefreshValidated(true);
                setRefreshDialogOpen(false);
                setRefreshPassword('');
                if (pendingSubmission) {
                    submitStockRecount(pendingSubmission, navigate);
                    setPendingSubmission(null);
                }
            } else {
                setRefreshError('Invalid password. Please try again.');
            }
        } catch (error) {
            setRefreshError(error?.response?.data?.message || 'Unable to verify password. Please try again.');
        } finally {
            setRefreshLoading(false);
        }
    };

    const changedRows = recountRows.filter((row) => row.delta !== 0);
    const unchangedRows = recountRows.filter((row) => row.delta === 0);

    return (
        <Container maxWidth="md">
            <Box
                sx={{
                    paddingTop: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Typography mt={2} variant="h4" textAlign="center" sx={{ color: 'var(--secColor)' }}>
                            Stock Recount
                        </Typography>
                        <Button
                            sx={main_button}
                            startIcon={<History />}
                            onClick={() => navigate('/ims/admin/stock-recount/history')}
                        >
                            View history
                        </Button>
                    </Box>
                    <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        Download on-hand quantities for a brand, enter actual counts, and submit to sync Shopify and the system.
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        {vendors && (
                            <Autocomplete
                                options={vendors}
                                onChange={(_, value) => setVendorId(value ? value._id : null)}
                                getOptionLabel={brandOptionLabel}
                                filterOptions={brandFilterOptions}
                                renderInput={(params) => (
                                    <MainInput
                                        {...params}
                                        margin="normal"
                                        fullWidth
                                        label="Select Brand"
                                    />
                                )}
                            />
                        )}
                        {vendorId && templateProducts.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography component="h2" variant="h6">
                                    Recount via Excel
                                </Typography>
                                <Tooltip title="Download all products with system on-hand (sorted by SKU)">
                                    <span>
                                        <Button
                                            fullWidth
                                            sx={{ my: 1, ...main_button }}
                                            onClick={handleExportToExcel}
                                            disabled={isDownloading || isLoading}
                                        >
                                            <Download /> Download template
                                        </Button>
                                    </span>
                                </Tooltip>
                                <ReactFileReader handleFiles={handleFiles} fileTypes={['.xlsx', '.xls']}>
                                    <Button fullWidth sx={{ my: 1, ...main_button }}>
                                        <Upload /> Upload .xlsx
                                    </Button>
                                </ReactFileReader>
                                {file && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Loaded: {file.name}
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {vendorId && isLoading && templateProducts.length === 0 && (
                            <Box sx={{ ...centered_flex_box, py: 3 }}>
                                <CircularProgress sx={{ color: 'var(--secColor)' }} />
                            </Box>
                        )}
                        {recountRows.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
                                    Recount preview
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                    <Chip label={`${recountRows.length} rows`} size="small" />
                                    <Chip label={`${changedRows.length} to update`} color="warning" size="small" />
                                    <Chip label={`${unchangedRows.length} unchanged`} size="small" />
                                </Box>
                                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>SKU</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell align="right">System On Hand</TableCell>
                                                <TableCell align="right">Actual Count</TableCell>
                                                <TableCell align="right">Delta</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sortProductsBySku(recountRows).map((row) => (
                                                <TableRow key={row.productId}>
                                                    <TableCell>{row.sku}</TableCell>
                                                    <TableCell>{row.name}</TableCell>
                                                    <TableCell align="right">{row.onHandBefore}</TableCell>
                                                    <TableCell align="right">{row.actualCount}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            color: row.delta > 0 ? 'success.main' : row.delta < 0 ? 'error.main' : 'text.secondary',
                                                            fontWeight: row.delta !== 0 ? 600 : 400,
                                                        }}
                                                    >
                                                        {row.delta > 0 ? `+${row.delta}` : row.delta}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                        {recountRows.length > 0 && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: 'warning.light',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <Warning sx={{ mt: 0.25 }} />
                                <Typography variant="body2">
                                    Submitting will update Shopify and system stock for changed products. You will be taken to a progress page — do not close it until the recount finishes.
                                </Typography>
                            </Box>
                        )}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2, ...main_button }}
                            disabled={isLoading || recountRows.length === 0 || changedRows.length === 0}
                        >
                            {isLoading ? (
                                <CircularProgress sx={{ color: 'white' }} />
                            ) : (
                                'Submit Stock Recount'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Dialog open={refreshDialogOpen} onClose={handleRefreshClose}>
                <DialogTitle>Outbound Password Required</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Please enter your outbound password to submit this stock recount.
                    </Typography>
                    <TextField
                        margin="normal"
                        label="Outbound Password"
                        type="password"
                        fullWidth
                        value={refreshPassword}
                        onChange={({ target }) => setRefreshPassword(target.value)}
                        autoFocus
                        error={Boolean(refreshError)}
                        helperText={refreshError}
                        disabled={refreshLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRefreshClose} disabled={refreshLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleRefreshConfirm} disabled={refreshLoading}>
                        {refreshLoading ? <CircularProgress size={20} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    token: state?.auth?.token,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
    vendors: state?.records?.vendors?.docs,
    stockRecountProducts: state?.records?.stockRecountProducts,
});

const mapDispatchToProps = { getVendors, getStockRecountTemplate, submitStockRecount };

export default connect(mapStateToProps, mapDispatchToProps)(StockRecount);
